import type { ShopBook } from "./library.ts";

/**
 * Tri et filtres de la bibliothèque, comme Studio (derniers chats, titre, auteur) et l'app (lus récemment, en cours,
 * extraits). L'ordre de la boutique départage les livres à égalité.
 */
export type LibrarySort = "recent" | "chats" | "title" | "author";
export type LibraryFilter = "all" | "reading" | "excerpts" | "smartbooks";

export const librarySorts: LibrarySort[] = ["recent", "chats", "title", "author"];
export const libraryFilters: LibraryFilter[] = ["all", "reading", "excerpts", "smartbooks"];

export interface BookActivity {
  /** Dernière lecture du livre (ms), d'après sa position synchronisée ; undefined s'il n'a jamais été ouvert. */
  readAt(book: ShopBook): number | undefined;
  /** Dernier message échangé sur le livre (ms) ; undefined sans discussion. */
  chatAt(book: ShopBook): number | undefined;
  /** Progression de 0 à 1 ; undefined si inconnue. */
  progress(book: ShopBook): number | undefined;
}

const authorOf = (book: ShopBook) => book.creators[0]?.displayName ?? "";

export function sortBooks(books: ShopBook[], sort: LibrarySort, activity: BookActivity, locale: string): ShopBook[] {
  const collator = new Intl.Collator(locale, { sensitivity: "base", numeric: true });
  const order = new Map(books.map((book, index) => [book.id, index]));
  const shop = (a: ShopBook, b: ShopBook) => order.get(a.id)! - order.get(b.id)!;
  const latest = (at: (book: ShopBook) => number | undefined) => (a: ShopBook, b: ShopBook) => (at(b) ?? 0) - (at(a) ?? 0);
  const compare: (a: ShopBook, b: ShopBook) => number =
    sort === "recent"
      ? latest(activity.readAt)
      : sort === "chats"
        ? latest(activity.chatAt)
        : sort === "title"
          ? (a, b) => collator.compare(a.title, b.title)
          : // Les livres sans auteur à la fin.
            (a, b) => Number(!authorOf(a)) - Number(!authorOf(b)) || collator.compare(authorOf(a), authorOf(b)) || collator.compare(a.title, b.title);
  return [...books].sort((a, b) => compare(a, b) || shop(a, b));
}

export function filterBooks(books: ShopBook[], filter: LibraryFilter, activity: BookActivity): ShopBook[] {
  switch (filter) {
    case "all":
      return books;
    case "reading":
      return books.filter((book) => {
        const progress = activity.progress(book);
        // Comme la barre de statut : « TERMINÉ » à la dernière page.
        return !book.isExtract && progress !== undefined && progress > 0 && progress < 1;
      });
    case "excerpts":
      return books.filter((book) => book.isExtract);
    case "smartbooks":
      return books.filter((book) => book.isSmartbook);
  }
}
